<template>
  <div class="sale-dashboard-view">
    <div class="card border-0">
      <div class="card-body border-0 pt-0 pb-3 pl-0 pr-0" style="background-color: #E3E8EE">
        <div class="general-info-wrapper">
          <amount-viewer :title="$t('Total Invoices')" :amount="generalInfoData.invoices" text-class="text-info"
                         v-animate-fade="0.1" :icon="'file-invoice'" :loading="generalInfoLoader"/>
          <amount-viewer :title="$t('Invoice Amount')" :icon="'money-bill'" :currency="sale.currency.symbol"
                         v-animate-fade="0.5" :amount="generalInfoData.invoice_amount" :loading="generalInfoLoader"/>
          <amount-viewer :title="$t('Paid Invoice Amount')" :icon="'money-bill'" text-class="text-success"
                         :currency="sale.currency.symbol" :amount="generalInfoData.paid_invoice_amount"
                         v-animate-fade="0.7" :loading="generalInfoLoader"/>
          <amount-viewer :title="$t('Outstanding Invoice Amount')" :icon="'money-bill'" text-class="text-danger"
                         :currency="sale.currency.symbol" :amount="generalInfoData.outstanding_invoice_amount"
                         v-animate-fade="0.9" :loading="generalInfoLoader"/>
          <amount-viewer :title="$t('Uninvoiced Sold Lots')" :icon="'file-invoice'" text-class="text-danger"
                         :amount="generalInfoData.uninvoiced_sold_lots"
                         v-animate-fade="1.1" :loading="generalInfoLoader"/>
          <amount-viewer :title="$t('Collected Lots')" :icon="'file-invoice'" text-class="text-primary"
                         :amount="generalInfoData.collected_lots"
                         v-animate-fade="1.1" :loading="generalInfoLoader"/>
          <amount-viewer :title="$t('Total Settlements')" :amount="generalInfoData.settlements" text-class="text-info"
                         v-animate-fade="0.3" :icon="'file-invoice'" :loading="generalInfoLoader"/>
          <amount-viewer :title="$t('Settlement Amount')" :icon="'money-bill'" :currency="sale.currency.symbol"
                         v-animate-fade="0.5" :amount="generalInfoData.settlement_amount" :loading="generalInfoLoader"/>
          <amount-viewer :title="$t('Paid Settlement Amount')" :icon="'money-bill'" text-class="text-success"
                         :currency="sale.currency.symbol" :amount="generalInfoData.paid_settlement_amount"
                         v-animate-fade="0.7" :loading="generalInfoLoader"/>
          <amount-viewer :title="$t('Outstanding Settlement Amount')" :icon="'money-bill'" text-class="text-danger"
                         :currency="sale.currency.symbol" :amount="generalInfoData.outstanding_settlement_amount"
                         v-animate-fade="0.9" :loading="generalInfoLoader"/>
          <amount-viewer :title="$t('Unsettled Sold Lots')" :icon="'file-invoice'" text-class="text-danger"
                         :amount="generalInfoData.unsettled_sold_lots"
                         v-animate-fade="1.1" :loading="generalInfoLoader"/>
          <amount-viewer :title="$t('Uncollected Lots')" :icon="'file-invoice'" text-class="text-primary"
                         :amount="generalInfoData.uncollected_lots"
                         v-animate-fade="1.1" :loading="generalInfoLoader"/>
        </div>
        <div class="main-wrapper">
          <div class="card" v-animate-fade="0.2">
            <div class="card-header d-flex align-items-center">
              <h5 class="d-inline-block mb-0">
                {{ $t('Sale Information') }}
              </h5>
              <b-button :disabled="sale.status === 'archived'" v-permission="'change_auction'" class="ml-auto p-0"
                        variant="link" @click="onEditClick">
                <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                {{ $t('Edit') }}
              </b-button>
            </div>
            <div class="card-body overflow-auto" style="max-height: 30em">
              <sale-info-table :sale="sale"/>
            </div>
          </div>
          <div class="card category-card" v-animate-fade="0.4">
            <div class="card-header">
              <h5 class="mb-0">{{ $t('Items by Category') }}</h5>
            </div>
            <no-data-available
              v-if="itemsByCategoryData.labels && !itemsByCategoryData.labels.length && !itemsByCategoryLoader"
              :height="5"/>
            <div v-else class="card-body d-flex align-items-center justify-content-center">
              <spinner :height="15" :show="itemsByCategoryLoader" :with-text="false"/>
              <e-pie-chart :chart-data="itemsByCategoryData"/>
            </div>
          </div>
          <div class="card profile-card" v-animate-fade="0.6">
            <div class="card-header">
              <h5 class="mb-0">{{ $t('Sale Profile') }}</h5>
            </div>
            <div class="card-body pb-0">
              <spinner :with-text="false" :show="profileInfoLoader" :height="15"/>
              <table v-if="!profileInfoLoader" class="table table-sm">
                <tr>
                  <td class="font-weight-bold border-top-0">{{ $t('Reserved Items') }}</td>
                  <td class="border-top-0">{{ profileInfoData.reserved_items }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">{{ $t('Lots') }}</td>
                  <td>{{ profileInfoData.lots }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">{{ $t('Estimate') }}</td>
                  <td>{{ profileInfoData.low_estimate_amount | asCurrency(sale.currency.symbol) }} -
                    {{ profileInfoData.high_estimate_amount | asCurrency(sale.currency.symbol) }}
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-bold">{{ $t('Reserve') }}</td>
                  <td>{{ profileInfoData.reserve | asCurrency(sale.currency.symbol) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">{{ $t('Sold Lots') }}</td>
                  <td>{{ profileInfoData.sold_lots }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">{{ $t('Unsold Lots') }}</td>
                  <td>{{ profileInfoData.unsold_lots }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">{{ $t('Highest Bids') }}</td>
                  <td>{{ profileInfoData.highest_bids | asCurrency(sale.currency.symbol) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">{{ $t('Sold Estimate') }}</td>
                  <td>{{ profileInfoData.sold_low_estimate_amount | asCurrency(sale.currency.symbol) }} -
                    {{ profileInfoData.sold_high_estimate_amount | asCurrency(sale.currency.symbol) }}
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-bold">{{ $t('Hammer') }}</td>
                  <td>{{ profileInfoData.hammer_price_amount | asCurrency(sale.currency.symbol) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">{{ $t('Hammer & BP') }}</td>
                  <td>{{ profileInfoData.hammer_price_amount + projectionInfoData.premium_amount | asCurrency(sale.currency.symbol) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">{{ $t('Average Lot Value') }}</td>
                  <td>
                    <div v-if="profileInfoData.sold_lots && profileInfoData.sold_lots > 0">
                      {{ (profileInfoData.hammer_price_amount + projectionInfoData.premium_amount) / profileInfoData.sold_lots | asCurrency(sale.currency.symbol) }}
                    </div>
                    <div v-else>
                      {{ 0 | asCurrency(sale.currency.symbol) }}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-bold">{{ $t('Average VC %') }}</td>
                  <td>
                    <div v-if="profileInfoData.hammer_price_amount && profileInfoData.hammer_price_amount > 0">
                      {{ (projectionInfoData.commission_amount / profileInfoData.hammer_price_amount) * 100 | asPercentage }}
                    </div>
                    <div v-else>
                      {{ 0.00 | asPercentage }}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-bold">{{ $t('Vendors') }}</td>
                  <td>{{ profileInfoData.vendors }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">{{ $t('Buyers') }}</td>
                  <td>{{ profileInfoData.buyers }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">{{ $t('Bidders') }}</td>
                  <td>{{ profileInfoData.bidders }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">{{ $t('Commission Bidders') }}</td>
                  <td>{{ profileInfoData.commission_bidders }}</td>
                </tr>
              </table>
            </div>
          </div>
          <div class="card terms-card" v-animate-fade="0.8">
            <div class="card-header">
              <h5 class="mb-0">{{ $t('Premiums') }}</h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-12">
                  <table v-if="calculatedPremiums && calculatedPremiums.length > 0" class="table mb-0 table-sm">
                    <tr>
                      <th class="border-top-0">{{ $t('Percentage') }}</th>
                      <th class="border-top-0" v-if="calculatedPremiums.length > 1">{{ $t('Over') }}</th>
                    </tr>
                    <tr v-for="(premium, index) in calculatedPremiums" :key="`premium-visual-tier-${index}`">
                      <td>{{ premium.percent | asPercentage }}</td>
                      <td v-if="calculatedPremiums.length > 1">
                        {{ premium.amount_over | asCurrency(sale.currency.symbol) }}
                      </td>
                    </tr>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div class="card collections-card" v-animate-fade="1">
            <div class="card-header d-flex align-items-center">
              <h5 class="d-inline-block mb-0">
                {{ $t('Collections') }}
              </h5>
              <b-button :disabled="sale.status === 'archived'" v-permission="'change_auction'" class="ml-auto p-0"
                        variant="link" @click="onAddCollectionClick">
                <font-awesome-icon icon="plus" style="font-size: .9em"></font-awesome-icon>
                {{ $t('Add New') }}
              </b-button>
            </div>
            <spinner :with-text="false" :show="collectionsLoader" :height="10"/>
            <div v-if="!collectionsLoader" class="card-body overflow-auto" style="max-height: 12em">
              <no-data-available v-if="collectionsData && collectionsData.length === 0" height="6"/>
              <table class="table table-sm mb-0">
                <tr v-for="(item, index) in collectionsData" :key="`auction-collection-row-${index}`">
                  <td :class="index === 0 ? 'border-top-0' : ''" style="width: 70%">
                    {{ item.translations | attributeByMasterLanguage('title') }}
                  </td>
                  <td :class="index === 0 ? 'border-top-0' : ''" class="text-right">
                    <b-button v-permission="'change_auction'" variant="primary" size="sm"
                              @click="onCollectionEditClick(item)">
                      <font-awesome-icon :icon="'pen'"></font-awesome-icon>
                    </b-button>
                    <b-button v-permission="'change_auction'" variant="danger" size="sm"
                              @click="onCollectionDeleteClick(item)" class="ml-2">
                      <font-awesome-icon :icon="'trash'"></font-awesome-icon>
                    </b-button>
                  </td>
                </tr>
              </table>
            </div>
          </div>
          <div class="projection-card" v-animate-fade="1.2">
            <div class="card">
              <div class="card-header">
                <h5 class="mb-0">{{ $t('Sale Income') }}</h5>
              </div>
              <spinner :with-text="false" :show="projectionInfoLoader" :height="10"/>
              <div v-if="!projectionInfoLoader" class="card-body">
                <table class="table table-sm mb-0">
                  <tr>
                    <td class="border-top-0 font-weight-bold">{{ $t('Premium') }}</td>
                    <td class="border-top-0">{{ projectionInfoData.premium_amount | asCurrency(sale.currency.symbol) }}
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">{{ $t('Commission') }}</td>
                    <td>{{ projectionInfoData.commission_amount | asCurrency(sale.currency.symbol) }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">{{ $t('Total Sale Income') }}</td>
                    <td>{{ projectionInfoData.commission_amount + projectionInfoData.premium_amount | asCurrency(sale.currency.symbol) }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">{{ $t('Average Lot Income') }}</td>
                    <td>
                      <div v-if="profileInfoData.sold_lots && profileInfoData.sold_lots > 0">
                        {{ (projectionInfoData.commission_amount + projectionInfoData.premium_amount) / profileInfoData.sold_lots | asCurrency(sale.currency.symbol) }}
                      </div>
                      <div v-else>
                        {{ 0 | asCurrency(sale.currency.symbol) }}
                      </div>
                    </td>
                  </tr>
                </table>
              </div>
            </div>
            <div class="card mt-3">
              <div class="card-header">
                <h5 class="mb-0">{{ $t('Sales Expense Projection') }}</h5>
              </div>
              <spinner :with-text="false" :show="projectionInfoLoader" :height="10"/>
              <div v-if="!projectionInfoLoader" class="card-body">
                <table class="table table-sm mb-0">
                  <tr>
                    <td class="border-top-0 font-weight-bold">{{ $t('Total Buyer VAT') }}</td>
                    <td class="border-top-0">{{ totalVat.total_buyer_vat | asCurrency(sale.currency.symbol) }}
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">{{ $t('Total Vendor VAT') }}</td>
                    <td>{{ totalVat.total_vendor_vat | asCurrency(sale.currency.symbol) }}</td>
                  </tr>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

</template>

<script>

  import {createNamespacedHelpers} from "vuex";
  import {SOURCE_SALES_DASHBOARD} from "../../../constants";
  import SaleInfoTable from "./SaleInfoTable";
  import Spinner from "../../../../core/components/common/Spinner";
  import AmountViewer from "../../../../core/components/common/AmountViewer";
  import NoDataAvailable from "../../../../core/components/common/NoDataAvailable";
  import EPieChart from "@/core/components/charts/EPieChart";
  import helperService from "@/core/services/helper.service";
  import cloneDeep from "lodash/cloneDeep";
  import {Formatter} from "@/core/services/formatter.service";


  const {mapActions, mapState} = createNamespacedHelpers('sales');
  /**
   * @author Saiat Kalbiev <kalbievich11@gmail.com>
   */
  export default {
    name: "SalesDashboard",
    components: {
      EPieChart,
      AmountViewer, Spinner, SaleInfoTable, NoDataAvailable
    },
    data: () => {
      return {};
    },
    computed: {
      ...mapState({
        sale: state => state.view.currentSale,
        generalInfoData: state => state.view.dashboardData.generalInfo.data,
        generalInfoLoader: state => state.view.dashboardData.generalInfo.loader,

        profileInfoData: state => state.view.dashboardData.profileInfo.data,
        profileInfoLoader: state => state.view.dashboardData.profileInfo.loader,

        projectionInfoData: state => state.view.dashboardData.projectionInfo.data,
        projectionInfoLoader: state => state.view.dashboardData.projectionInfo.loader,

        itemsByCategoryLoader: state => state.view.dashboardData.itemsByCategory.loader,
        itemsByCategoryData: state => state.view.dashboardData.itemsByCategory.data,

        collectionsLoader: state => state.view.dashboardData.collections.loader,
        collectionsData: state => state.view.dashboardData.collections.data,

        totalVatLoader: state => state.view.dashboardData.totalVat.loader,
        totalVat: state => state.view.dashboardData.totalVat.data,
      }),
      calculatedPremiums() {
        if (this.sale && this.sale.premiums) {
          let data = helperService.reverseCalculateTiers({premiums: cloneDeep(this.sale.premiums)})
          return data.premiums
        }
        return []
      }
    },
    methods: {
      ...mapActions(['showModal', 'getDashboardData', 'showCollectionModal', 'deleteSaleCollection', 'getModalObject']),
      onEditClick() {
        this.showModal()
        this.getModalObject({uuid: this.sale.uuid, source: SOURCE_SALES_DASHBOARD})
      },
      onAddCollectionClick() {
        this.showCollectionModal({
          source: SOURCE_SALES_DASHBOARD,
          sale: this.sale
        })
      },
      async onCollectionDeleteClick(collection) {
        this.$confirm(
          this.$i18n.t("Are you sure you want to delete collection: {title}?",
            {title: Formatter.attributeByMasterLanguage(collection.translations, "title")}
          ),
          this.$i18n.t('Collection was successfully deleted'),
          this.deleteSaleCollection,
          collection,
        );
      },
      onCollectionEditClick(collection) {
        this.showCollectionModal({
          source: SOURCE_SALES_DASHBOARD,
          sale: this.sale,
          object: collection
        })
      }
    },
    mounted() {
      this.getDashboardData({
        auction_uuid: this.sale.uuid
      });
    }
  }

</script>

<style lang="scss" scoped>
  @import "./../../scss/variables";

  .general-info-wrapper {
    display: grid;
    grid-gap: 1em;
    grid-template-columns: repeat(6, 1fr);
  }

  .main-wrapper {
    display: grid;
    grid-gap: 1em;
    margin-top: 1em;
    grid-template-columns: repeat(3, 1fr);

    .terms-card {
    }
  }

  @media only screen and (max-width: map-get($grid-breakpoints, "xl")) {
    .general-info-wrapper {
      grid-template-columns: 2fr 2fr;
    }
    .main-wrapper {
      grid-template-columns: 2fr 2fr;

      .profile-card {
        grid-column: 2/3;
        grid-row: 1/2;
      }

      .terms-card {
        grid-column: 1/2;
      }

      .projection-card {
        grid-column: 2/3;
        grid-row: 2/3;
      }
    }
  }

</style>
